import {
  Typography,
  TextField,
  Button,
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Switch,
  MenuItem,
  Menu,
  Divider,
  Badge,
  CircularProgress,
  Dialog,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Popper,
  Paper,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import TuneIcon from "@mui/icons-material/Tune";
import SettingsIcon from "@mui/icons-material/Settings";
import defectOptions from "../../datas/defectOptions.json";
import { useEffect, useRef, useState } from "react";
import { continueTrainState, parentModelState, projectInfoState } from "../../recoil";
import { useRecoilState, useRecoilValue } from "recoil";
import SelectionInspModel from "./SelectionInspModel";
import AugOptionSection from "./AugOptionSection";
import { useAugOptions } from "../../hooks/useAugOptions";
import { feature } from "../../services/webConfig";
import { CompareBasketPopover, DatasetImage, fileName, useCompareBasket } from "./CompareBasket";
import CollectionsIcon from "@mui/icons-material/Collections";
import { descType, useAIModuleSync } from "../../hooks/useAIModuleSync";
import { useParsedModels } from "../../hooks/useParsedModels";
import { useHistoryMap } from "../../hooks/useHistoryMap";
import { useEpochList } from "../../hooks/useEpochList";
import { clearImgData, getDatasetInfo, getFile, sendImgDataFromStorage } from "common-lib";
import { getImgData } from "../../services/fileService";
import { fetchProjectInspModels } from "../../services/inspModelCache";
import { fetchModuleServer } from "../../services/aiServer";
import { extractImgPaths } from "../../utils/datasetFileUtil";
import { useParams } from "react-router-dom";
import { DefectOptionItem, InspModelItem } from "@types";
import { continueTrainInfoState, myAIModuleState, trainUuidState } from "../../recoil/upgrade";
import { useTutorial } from "../../context/TutorialContext";
import { TUTORIALS } from "../../tutorial";
import { t } from "i18next";
import { toast } from "react-toastify";
import { editProjectConfig, getProjectGeneralInfo } from "../../services/projectService";
import {
  DEFAULT_AUTO_TRAIN_CONDITION,
  InspAutoTrainCondition,
  InspAutoTrainConfig,
} from "../../utils/autoTrainSchedule";
import { ChevronDown, ChevronUp, MaximizeIcon } from "lucide-react";

interface DefectPreviewProps {
  dataSetId: number;
  openDefectPreview: boolean;
  setOpenDefectPreview: (open: boolean) => void;
  onTrain?: (random: number, saveOption: SaveOptionType) => void | Promise<void>;
  epoch?: number;
  setEpoch?: (epoch: number) => void;
  trainList?: any[];
  selectedTrainCount?: number;
  setSelectedTrainCount?: (count: number) => void;
  overkillCondition: number;
  setOverkillCondition: (condition: number) => void;
}

const WEEKDAYS = ["일", "월", "화", "수", "목", "금", "토"];

const PREVIEW_COUNT = 3;

type PreviewResult = {
  path: string;
  origin: string | null;
  process: string | null;
  defect: string | null; // 디펙 삽입 이미지 위에 label_seg 디펙 픽셀을 빨갛게 칠한 이미지
  noLabelSeg?: boolean; // 응답은 왔는데 label_seg가 없음 = 선택된 모델이 디펙 위치를 지원하지 않음
};

const loadImage = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

// label_seg 마스크(채널별 흑백 PNG)에서 디펙인 픽셀을 디펙 삽입 이미지 위에 빨갛게 칠한다.
// 채널 순서/개수를 가정하지 않고, 채널마다 소수 쪽 값(배경 채널이면 검정, 디펙 채널이면 흰색)인 픽셀을 디펙으로 본다
const drawDefectMask = async (base: string, masks: string[]) => {
  const [img, ...maskImgs] = await Promise.all([base, ...masks].map(loadImage));
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
  const pixels = (el: HTMLImageElement) => {
    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(el, 0, 0, w, h);
    return ctx.getImageData(0, 0, w, h).data;
  };
  const maskPixels = maskImgs.map(pixels);
  const whiteCounts = maskPixels.map((m) => {
    let n = 0;
    for (let i = 0; i < m.length; i += 4) if (m[i] > 127) n++;
    return n;
  });
  // 흰색이 절반 넘는 채널은 검정 쪽이 디펙
  const defectIsWhite = whiteCounts.map((n) => n <= (w * h) / 2);
  ctx.clearRect(0, 0, w, h);
  ctx.drawImage(img, 0, 0);
  const out = ctx.getImageData(0, 0, w, h);
  const d = out.data;
  for (let i = 0; i < d.length; i += 4) {
    if (!maskPixels.some((m, c) => m[i] > 127 === defectIsWhite[c])) continue;
    d[i] = d[i] * 0.4 + 255 * 0.6;
    d[i + 1] *= 0.4;
    d[i + 2] *= 0.4;
  }
  ctx.putImageData(out, 0, 0);
  return canvas.toDataURL();
};

export type SaveOptionType = "OVERKILL_THRESHOLD" | "SAVE_EVERY_N_EPOCHS";

type LimitEntry = ["range" | "select", "int" | "float" | "string", number[] | string[]];
type LimitMap = Record<string, { limit: Record<string, LimitEntry> }>;

export default function DefectPreview({
  openDefectPreview,
  setOpenDefectPreview,
  onTrain,
  epoch,
  setEpoch,
  trainList,
  selectedTrainCount,
  setSelectedTrainCount,
  overkillCondition,
  setOverkillCondition,
  dataSetId,
}: DefectPreviewProps) {
  const { id } = useParams();
  const myAIModule = useRecoilValue(myAIModuleState);
  const trainUuid = useRecoilValue(trainUuidState);
  const projectData = useRecoilValue(projectInfoState);

  // 디펙 옵션/미리보기 노출 (web-config.json AUGMENTATION.OPTION_VIEW). 끄면 학습 조건만 남는다
  const augView = feature("AUGMENTATION.OPTION_VIEW");

  const [previews, setPreviews] = useState<PreviewResult[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(true);
  const [previewMsg, setPreviewMsg] = useState<string | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  // 풀스크린에서 볼 이미지: "all" = 3장 모두, 숫자 = 해당 한 장
  const [fullscreenRow, setFullscreenRow] = useState<"all" | number>("all");
  const [inspModelList, setInspModelList] = useState<InspModelItem[]>([]);

  const parsedModels = useParsedModels(inspModelList);
  const historyMap = useHistoryMap(parsedModels);
  const epochList = useEpochList(parsedModels);

  // 옵션 편집 상태: 옵션 설정 칸과 이미지 풀스크린의 옵션 패널이 같이 쓴다
  const augOptions = useAugOptions();
  // 이미지 풀스크린에서 여는 옵션 패널 (모달이 아니라 떠 있는 패널 → 열어 둔 채 새로고침 가능)
  const [fsOptionAnchor, setFsOptionAnchor] = useState<HTMLElement | null>(null);
  // 풀스크린 비교: 행(이미지 1~3)별로 원본 옆에 띄울 담긴 이미지 경로, 정렬 방향, 우클릭 메뉴 위치
  const basket = useCompareBasket(String(id));
  const [compareByRow, setCompareByRow] = useState<Record<number, string>>({});
  const [compareLayout, setCompareLayout] = useState<"row" | "column">("row");
  const [compareMenu, setCompareMenu] = useState<{ row: number; x: number; y: number } | null>(null);
  const [fsBasketAnchor, setFsBasketAnchor] = useState<HTMLElement | null>(null);
  const syncModule = useAIModuleSync();
  const [saveOption, setSaveOption] = useState<SaveOptionType>("OVERKILL_THRESHOLD");
  const [epochInterval, setEpochInterval] = useState<number>(10);
  const parentModel = useRecoilValue(parentModelState);
  const [savedAutoTrain, setSavedAutoTrain] = useState<InspAutoTrainConfig | null>(null);
  const [autoTrainEnabled, setAutoTrainEnabled] = useState(false);
  const [autoTrainCondition, setAutoTrainCondition] = useState<InspAutoTrainCondition>(DEFAULT_AUTO_TRAIN_CONDITION);

  const fetchAutoTrainConfig = async () => {
    const { project } = await getProjectGeneralInfo(Number(id));
    const config = typeof project?.config === "string" ? JSON.parse(project.config) : project?.config;
    const saved: InspAutoTrainConfig | undefined = config?.autoTrain;
    if (!saved || typeof saved !== "object") return; // 구버전 config.autoTrain(boolean) 무시
    setSavedAutoTrain(saved);
    setAutoTrainEnabled(saved.enabled);
    setAutoTrainCondition(saved.condition);
  };

  const saveAutoTrain = async (enabled: boolean) => {
    const autoTrain: InspAutoTrainConfig = {
      enabled,
      condition: autoTrainCondition,
      serverAddress: myAIModule?.serverAddress ?? savedAutoTrain?.serverAddress ?? "",
      train: {
        epoch: epoch ?? 10,
        imgPerEpoch: selectedTrainCount ?? 0,
        imgCount: projectData?.counts.total_count ?? 0,
        overkillCondition,
        parentModel,
      },
      savedAt: Date.now(),
      lastRunAt: savedAutoTrain?.lastRunAt ?? null,
    };
    await editProjectConfig(Number(id), { autoTrain });
    setSavedAutoTrain(autoTrain);
  };

  // 학습은 AI 서버에 저장된(Save한) 옵션으로 진행한다. augModes도 Save 때만 저장 → 저장 안 한 on/off가 학습 완료 복원에 섞이지 않는다
  const handleProceed = async () => {
    try {
      if (autoTrainEnabled) {
        await saveAutoTrain(true);
        const { type, week, dayOfWeek, time } = autoTrainCondition;
        toast.success(
          `자동학습 예약: ${type === "weekly" ? "매주" : `매월 ${week}번째`} ${WEEKDAYS[dayOfWeek]}요일 ${time}`,
        );
        setOpenDefectPreview(false);
        return;
      }
      if (savedAutoTrain?.enabled) await saveAutoTrain(false);
    } catch (error) {
      console.error(error);
      toast.error(t("AutoTrainSaveFailMsg", "자동학습 설정 저장에 실패했습니다."));
      return;
    }
    setOpenDefectPreview(false);
    if (onTrain) onTrain(selectedTrainCount || 0, saveOption);
  };

  const { run, nextSubStep, tutorialKey, stepIndex } = useTutorial();

  const fetchInspModel = async () => {
    try {
      setInspModelList(await fetchProjectInspModels(Number(id)));
    } catch (error: any) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchInspModel();
    fetchAutoTrainConfig().catch(console.error);
  }, []);

  const rowStyle = {
    display: "flex",
    alignItems: "center",
    gap: 1,
    flexWrap: "wrap",
    mb: 1.5,
  };

  const inputStyle = {
    width: 60,
  };

  useEffect(() => {
    if (!run) return;

    const currentSubStep = TUTORIALS[tutorialKey][stepIndex];
    if (currentSubStep?.nextTrigger !== "#model-save-option") return;

    if (selectedTrainCount === 10 && epoch === 2) {
      nextSubStep();
    }
  }, [run, stepIndex, selectedTrainCount, epoch, nextSubStep]);

  const handleApply = async () => {
    if (!myAIModule || !trainUuid) {
      toast.error(t("NoAIModuleMsg", "연결된 AI 모듈이 없습니다."));
      return;
    }
    // 저장 안 한 옵션 변경이 있으면 먼저 Save(/sync) → 바꾼 옵션으로 바로 미리보기
    if (augOptions.dirty && !(await augOptions.save())) return;
    const serverAddress = myAIModule.serverAddress;

    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewMsg(null);
    setPreviews([]);
    try {
      const dsInfo = await getDatasetInfo(dataSetId);
      const dsFileInfo = await getFile(dsInfo.data.path);
      const allPaths = Array.from(
        new Set(Object.values(extractImgPaths(dsFileInfo.data) as Record<string, string[]>).flat()),
      );
      if (!allPaths.length) {
        setPreviewMsg(t("NoPreviewImageMsg", "미리볼 이미지가 없습니다."));
        return;
      }
      const targets = [...allPaths].sort(() => Math.random() - 0.5).slice(0, PREVIEW_COUNT);

      const before = (await fetchModuleServer(id)).module;
      // makelabel(ConvertMat2Bytes): augmentation 라벨 → label_seg
      const previewOptions = Object.fromEntries(
        Object.entries(before.options as Record<string, DefectOptionItem>).map(([key, item]) => {
          const { name, para, tag = [] } = item;
          const mode = tag.includes("input")
            ? "backup" // 원본 이미지도 받는다
            : tag.includes("model")
              ? "infer"
              : tag.includes("classify") || name === "MakeScoreMap"
                ? "disabled"
                : name === "ConvertData2Bytes" || name === "ConvertMat2Bytes"
                  ? "enabled"
                  : para?.mode;
          // mode가 원래 없는 모듈(output 등)에는 키를 만들지 않는다
          return [key, { ...item, para: mode === undefined ? para : { ...para, mode } }];
        }),
      );

      // 이미지 출력 큐 가 비어 있는지 확인하고, 다른 이미지 결과가 오면 재요청함
      const fetchView = async (path: string) => {
        try {
          for (let attempt = 0; attempt < 3; attempt++) {
            await clearImgData(serverAddress, trainUuid).catch(console.error);
            await sendImgDataFromStorage(serverAddress, {
              uuid: trainUuid,
              path,
              destination: {
                index1: "tag",
                index2: "image_bytes_original",
                index3: "image_bytes_process",
                index4: "label_seg",
              },
            });
            const res = await getImgData(serverAddress, trainUuid, false, "");
            if (!res?.image_id || res.image_id === path) return res;
            console.warn("다른 이미지 결과를 받아 재요청합니다", { requested: path, received: res.image_id });
          }
          return null;
        } catch (error) {
          console.error(error);
          return null;
        } finally {
          await clearImgData(serverAddress, trainUuid).catch(console.error);
        }
      };

      await syncModule(previewOptions, "preview");
      try {
        const results: PreviewResult[] = [];
        for (const path of targets) {
          const res = await fetchView(path);
          const process = typeof res?.processImage === "string" ? res.processImage : null;
          // label_seg는 라벨 채널별 마스크 배열
          const seg: string[] = Array.isArray(res?.label_seg) ? res.label_seg : res?.label_seg ? [res.label_seg] : [];
          let defect: string | null = null;
          if (process && seg.length) {
            defect = await drawDefectMask(process, seg).catch((e) => {
              console.error(e);
              return null;
            });
          } else {
            console.warn("응답에 label_seg가 없습니다", path, res);
          }
          results.push({ path, origin: res?.originImage ?? null, process, defect, noLabelSeg: !!res && !seg.length });
          setPreviews([...results]); // 도착하는 대로 보여준다
        }
      } finally {
        await syncModule(before.options, descType(before.desc), before.desc).catch(console.error);
      }
    } catch (error: any) {
      console.error(error);
      setPreviewMsg(error?.response?.data?.error ?? error?.message ?? "미리보기를 불러오지 못했습니다.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const syncModuleOnly = async () => {
    const before = (await fetchModuleServer(id)).module;
    await syncModule(before.options, descType(before.desc), before.desc);
  };

  const autoLoaded = useRef(false);
  useEffect(() => {
    if (autoLoaded.current || !myAIModule || !trainUuid) return;
    autoLoaded.current = true;
    if (augView) handleApply();
    else syncModuleOnly().catch(console.error);
  }, [augView, myAIModule, trainUuid]);

  // 도착한 결과 + 아직 오는 중인 자리(로딩) + 시작 전 안내 한 줄
  const previewRows: (PreviewResult | "loading" | "empty")[] = [
    ...previews,
    ...Array.from({ length: previewLoading ? PREVIEW_COUNT - previews.length : 0 }, () => "loading" as const),
    ...(!previewLoading && !previews.length ? ["empty" as const] : []),
  ];

  // 원본 / 미리보기 / 디펙 위치 3열 그리드
  const renderOriginCell = (row: PreviewResult, absIndex: number) => {
    const comparePath = compareByRow[absIndex];
    const originImg = row.origin ? (
      <img src={row.origin} alt="" title={row.path} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
    ) : (
      <Typography variant="caption" sx={{ color: "#aeaeb2" }}>
        {t("NoImage")}
      </Typography>
    );
    if (!comparePath) return originImg;
    const pane = (label: string, content: React.ReactNode) => (
      <Box
        sx={{
          position: "relative",
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {content}
        <Typography
          variant="caption"
          noWrap
          sx={{
            position: "absolute",
            top: 4,
            left: 4,
            maxWidth: "90%",
            px: 0.75,
            borderRadius: 1,
            color: "#fff",
            bgcolor: "rgba(0,0,0,0.6)",
          }}
        >
          {label}
        </Typography>
      </Box>
    );
    return (
      <Box sx={{ display: "flex", flexDirection: compareLayout, width: "100%", height: "100%", gap: 0.5 }}>
        {pane(t("Original", "원본"), originImg)}
        {pane(fileName(comparePath), <DatasetImage path={comparePath} sx={{ width: "100%", height: "100%" }} />)}
      </Box>
    );
  };

  // rowOffset: 풀스크린에서만 넘긴다 → 원본 칸 우클릭으로 비교 이미지 띄우기 (rows가 잘려 있어도 원래 행 번호로 저장)
  const renderPreviewGrid = (rows: typeof previewRows, cellHeight: number | string, rowOffset?: number) => (
    // cellHeight "100%" = 부모 높이를 행 수로 나눠 꽉 채움 (스크롤 없음)
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: 1.5,
        ...(cellHeight === "100%" && {
          height: "100%",
          gridTemplateRows: `auto repeat(${rows.length}, 1fr)`,
        }),
      }}
    >
      {[t("Original", "원본"), t("Preview", "미리보기"), t("DefectLocation", "디펙 위치")].map((title) => (
        <Typography key={title} variant="caption" color="text.secondary">
          {title}
        </Typography>
      ))}
      {rows.map((row, i) =>
        [0, 1, 2].map((col) => (
          <Box
            key={`${i}-${col}`}
            onContextMenu={
              rowOffset !== undefined && col === 0 && typeof row === "object"
                ? (e) => {
                    e.preventDefault();
                    setCompareMenu({ row: rowOffset + i, x: e.clientX, y: e.clientY });
                  }
                : undefined
            }
            sx={{
              position: "relative",
              height: cellHeight,
              minHeight: 0,
              borderRadius: 2,
              overflow: "hidden",
              border: "1px solid var(--dk-border, #ddd)",
              background: "#000",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {row === "loading" ? (
              <CircularProgress size={24} />
            ) : row === "empty" ? (
              <Typography variant="caption" sx={{ color: "#aeaeb2", px: 1, textAlign: "center" }}>
                {previewMsg ?? t("PreviewHintMsg", "옵션 적용을 눌러 확인하세요")}
              </Typography>
            ) : col === 0 ? (
              renderOriginCell(row, (rowOffset ?? 0) + i)
            ) : [row.origin, row.process, row.defect][col] ? (
              <img
                src={[row.origin, row.process, row.defect][col]!}
                alt=""
                title={row.path}
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            ) : (
              <Typography variant="caption" sx={{ color: "#aeaeb2", px: 1, textAlign: "center" }}>
                {col === 2 && row.noLabelSeg
                  ? t("LabelSegUnsupportedMsg", "현재 선택된 모델은 해당 기능을 지원하지 않습니다.")
                  : t("NoImage")}
              </Typography>
            )}
          </Box>
        )),
      )}
    </Box>
  );

  return (
    <div id="defect-preview-modal" style={overlayStyle}>
      <div style={{ ...modalStyle, width: augView ? 1100 : 560 }}>
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: "1px solid var(--dk-border, #eee)",
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            {augView ? t("DefectImagePreview") : t("TrainSetting", "학습 설정")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {augView ? t("DefectPreviewDescMsg") : t("TrainSettingDescMsg", "학습에 사용할 조건을 확인하세요.")}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, p: 3, overflowY: "auto" }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: augView ? "3fr 1.2fr" : "1fr",
              gap: 3,
            }}
          >
            {augView && <AugOptionSection ctrl={augOptions} disabled={previewLoading} maxHeight="55vh" />}

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box id="train-option" sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box
                  sx={{
                    border: "1px solid #90caf9",
                    background: "#f5faff",
                    borderRadius: 2,
                    p: 2,
                    height: "fit-content",
                  }}
                >
                  <Typography fontWeight={600} mb={1}>
                    {t("TrainImageCount")}
                  </Typography>
                  <TextField
                    id="train-img-count-input"
                    fullWidth
                    size="small"
                    label={t("TrainImageCount")}
                    type="number"
                    placeholder={t("TrainImageCountPlaceholder")}
                    helperText={`${t("TotalImageCount")}: ${trainList?.length ?? 0}`}
                    value={selectedTrainCount}
                    inputProps={{
                      min: 0,
                      max: trainList?.length ?? 0,
                    }}
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10);
                      const max = trainList?.length ?? 0;

                      if (setSelectedTrainCount) {
                        if (isNaN(val)) {
                          setSelectedTrainCount(0);
                        } else {
                          setSelectedTrainCount(Math.min(val, max));
                        }
                      }
                    }}
                  />
                </Box>
                <Box
                  id="epoch-option"
                  sx={{
                    border: "1px solid #90caf9",
                    background: "#f5faff",
                    borderRadius: 2,
                    p: 2,
                    height: "fit-content",
                  }}
                >
                  <Typography fontWeight={600} mb={1}>
                    {t("Epoch")} {t("Setting")}
                  </Typography>
                  <TextField
                    id="epoch-count-input"
                    fullWidth
                    size="small"
                    label={t("Epoch")}
                    type="number"
                    placeholder={t("TrainImageCountPlaceholder")}
                    helperText={t("RecommendedRangeMsg")}
                    value={epoch}
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10);
                      if (setEpoch) setEpoch(isNaN(val) ? 0 : val);
                    }}
                  />
                </Box>
              </Box>
              <Box
                id="model-save-option"
                sx={{
                  border: "1px solid #90caf9",
                  background: "#f5faff",
                  borderRadius: 2,
                  p: 2,
                  height: "fit-content",
                }}
              >
                <Typography fontWeight={600} mb={2}>
                  {t("ModelSaveCondition")}
                </Typography>

                <RadioGroup value={saveOption} onChange={(e) => setSaveOption(e.target.value as SaveOptionType)}>
                  <Box sx={rowStyle}>
                    <Radio value="OVERKILL_THRESHOLD" />
                    <Typography>{t("OverkillRate")}</Typography>
                    <TextField
                      id="overkill-threshold-input"
                      size="small"
                      type="number"
                      sx={inputStyle}
                      disabled={saveOption !== "OVERKILL_THRESHOLD"}
                      value={overkillCondition}
                      onChange={(e) => setOverkillCondition(Number(e.target.value))}
                      inputProps={{ min: 0, max: 100 }}
                    />
                    <Typography>{t("PercentOrLess")}</Typography>
                  </Box>
                  <Box sx={rowStyle}>
                    <Radio value="SAVE_EVERY_N_EPOCHS" />
                    <TextField
                      size="small"
                      type="number"
                      sx={inputStyle}
                      disabled={saveOption !== "SAVE_EVERY_N_EPOCHS"}
                      value={epochInterval}
                      onChange={(e) => setEpochInterval(Number(e.target.value))}
                      inputProps={{ min: 1 }}
                    />
                    <Typography>{t("OncePerEpochMsg")}</Typography>
                  </Box>
                </RadioGroup>
              </Box>
              <Box
                sx={{
                  border: "1px solid #90caf9",
                  background: "#f5faff",
                  borderRadius: 2,
                  p: 2,
                  height: "fit-content",
                }}
              >
                <Typography fontWeight={600} mb={2}>
                  {t("ContinueTraining")}
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    justifyContent: "space-between",
                    px: 1,
                    py: 1,
                    borderRadius: 2,
                    background: "var(--dk-surface, #ffffff)",
                    border: "1px solid #e3f2fd",
                  }}
                >
                  <SelectionInspModel historyMap={historyMap} epochList={epochList} inspModelList={inspModelList} />
                </Box>
              </Box>
              <Box
                id="auto-train-option"
                sx={{
                  border: "1px solid #90caf9",
                  background: "#f5faff",
                  borderRadius: 2,
                  p: 2,
                  height: "fit-content",
                }}
              >
                <FormControlLabel
                  sx={{ m: 0, width: "100%", justifyContent: "space-between" }}
                  labelPlacement="start"
                  label={<Typography fontWeight={600}>{t("AutoTraining", "자동학습")}</Typography>}
                  control={
                    <Switch checked={autoTrainEnabled} onChange={(e) => setAutoTrainEnabled(e.target.checked)} />
                  }
                />
                {autoTrainEnabled && (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1 }}>
                    <RadioGroup
                      row
                      value={autoTrainCondition.type}
                      onChange={(e) =>
                        setAutoTrainCondition((prev) => ({
                          ...prev,
                          type: e.target.value as InspAutoTrainCondition["type"],
                        }))
                      }
                    >
                      <FormControlLabel value="weekly" control={<Radio size="small" />} label={t("Weekly", "매주")} />
                      <FormControlLabel value="monthly" control={<Radio size="small" />} label={t("Monthly", "매월")} />
                    </RadioGroup>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      {autoTrainCondition.type === "monthly" && (
                        <TextField
                          select
                          size="small"
                          label={t("Week", "주차")}
                          sx={{ flex: 1 }}
                          value={autoTrainCondition.week}
                          onChange={(e) => setAutoTrainCondition((prev) => ({ ...prev, week: Number(e.target.value) }))}
                        >
                          {[1, 2, 3, 4, 5].map((w) => (
                            <MenuItem key={w} value={w}>
                              {w}주
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                      <TextField
                        select
                        size="small"
                        label={t("DayOfWeek", "요일")}
                        sx={{ flex: 1 }}
                        value={autoTrainCondition.dayOfWeek}
                        onChange={(e) =>
                          setAutoTrainCondition((prev) => ({ ...prev, dayOfWeek: Number(e.target.value) }))
                        }
                      >
                        {WEEKDAYS.map((d, i) => (
                          <MenuItem key={i} value={i}>
                            {d}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                    <TextField
                      size="small"
                      type="time"
                      label={t("Time", "시간")}
                      InputLabelProps={{ shrink: true }}
                      value={autoTrainCondition.time}
                      onChange={(e) =>
                        e.target.value && setAutoTrainCondition((prev) => ({ ...prev, time: e.target.value }))
                      }
                      helperText={t(
                        "AutoTrainHelperMsg",
                        "학습 진행 시 즉시 학습하지 않고 설정한 시각에 학습을 요청합니다.",
                      )}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>

        {augView && (
          <Box
            sx={{
              flexShrink: 0,
              mx: 3,
              mb: 2,
              border: "1px solid var(--dk-border, #e0e0e0)",
              borderRadius: 2,
              p: 2,
              background: "var(--dk-surface, #fff)",
              boxShadow: "0 -10px 20px -14px rgba(0,0,0,0.4)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: previewOpen ? 1 : 0,
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer" }}
                onClick={() => setPreviewOpen((v) => !v)}
              >
                <IconButton size="small">
                  {previewOpen ? <ChevronDown size="18px" /> : <ChevronUp size="18px" />}
                </IconButton>
                <Typography fontWeight={600}>{t("ImagePreview")}</Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Tooltip title={t("RefreshPreview", "옵션 저장하고 미리보기 새로고침")}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={handleApply}
                      disabled={previewLoading || augOptions.saving || augOptions.optionsLoading}
                    >
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={"전체 화면으로 보기"}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setFullscreenRow(0);
                      setFullscreen(true);
                    }}
                    disabled={!previews.length && !previewLoading}
                  >
                    <MaximizeIcon fontSize="small" size="18px" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {previewOpen && renderPreviewGrid(previewRows, 110)}
          </Box>
        )}

        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid var(--dk-border, #eee)",
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
          }}
        >
          <Button onClick={() => setOpenDefectPreview(false)}>{t("Cancel")}</Button>
          <Button id="start-epoch-train" variant="contained" onClick={handleProceed}>
            {autoTrainEnabled ? t("ReserveTraining", "학습 예약") : t("ProceedWithTraining")}
          </Button>
        </Box>
      </div>
      <Dialog
        fullScreen
        open={fullscreen}
        onClose={() => {
          setFullscreen(false);
          setFsOptionAnchor(null);
        }}
        sx={{ zIndex: 1400 }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            px: 3,
            py: 1,
            borderBottom: "1px solid var(--dk-border, #eee)",
          }}
        >
          <Typography fontWeight={700}>{t("ImagePreview")}</Typography>
          <Tooltip title={t("RefreshPreview", "옵션 저장 후 이미지 새로고침")}>
            <span>
              <IconButton
                size="small"
                onClick={handleApply}
                disabled={previewLoading || augOptions.saving || augOptions.optionsLoading}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t("OptionSetting")}>
            <IconButton
              size="small"
              color={fsOptionAnchor ? "primary" : "default"}
              onClick={(e) => setFsOptionAnchor(fsOptionAnchor ? null : e.currentTarget)}
            >
              <TuneIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {/* disablePortal: Dialog 안에 그려야 Dialog의 포커스 고정에 입력이 막히지 않는다 */}
          <Popper
            open={!!fsOptionAnchor}
            anchorEl={fsOptionAnchor}
            placement="bottom-start"
            disablePortal
            sx={{ zIndex: 10 }}
          >
            <Paper elevation={8} sx={{ width: 560, maxWidth: "90vw", mt: 1 }}>
              <AugOptionSection ctrl={augOptions} disabled={previewLoading} maxHeight="65vh" />
            </Paper>
          </Popper>
          <Tooltip title={t("CompareBasket", "비교 이미지 담기")}>
            <IconButton size="small" onClick={(e) => setFsBasketAnchor(e.currentTarget)}>
              <Badge badgeContent={basket.items.length} color="primary" max={99}>
                <CollectionsIcon fontSize="small" />
              </Badge>
            </IconButton>
          </Tooltip>
          <CompareBasketPopover
            projectId={String(id)}
            anchorEl={fsBasketAnchor}
            onClose={() => setFsBasketAnchor(null)}
            zIndex={1500}
          />
          <ToggleButtonGroup
            size="small"
            exclusive
            value={fullscreenRow}
            onChange={(_, v) => v !== null && setFullscreenRow(v)}
          >
            <ToggleButton value="all">{t("All", "전체")}</ToggleButton>
            {previewRows.map((row, i) => (
              <ToggleButton key={i} value={i} disabled={row === "empty"}>
                {t("Image", "이미지")} {i + 1}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          {fullscreenRow !== "all" && typeof previewRows[fullscreenRow] === "object" && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
              {(previewRows[fullscreenRow] as PreviewResult).path}
            </Typography>
          )}
          <Box sx={{ flex: 1 }} />
          <IconButton
            onClick={() => {
              setFullscreen(false);
              setFsOptionAnchor(null);
            }}
            aria-label="close"
          >
            ✕
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, p: 2, overflow: "hidden" }}>
          {renderPreviewGrid(
            fullscreenRow === "all" ? previewRows : previewRows.slice(fullscreenRow, fullscreenRow + 1),
            "100%",
            fullscreenRow === "all" ? 0 : fullscreenRow,
          )}
          <Menu
            open={!!compareMenu}
            onClose={() => setCompareMenu(null)}
            anchorReference="anchorPosition"
            anchorPosition={compareMenu ? { top: compareMenu.y, left: compareMenu.x } : undefined}
            sx={{ zIndex: 1500 }}
          >
            <MenuItem disabled dense sx={{ fontSize: 12 }}>
              {t("ShowCompareImage", "비교할 이미지 띄우기")}
            </MenuItem>
            {basket.items.length ? (
              basket.items.map((item) => (
                <MenuItem
                  key={item.path}
                  dense
                  selected={compareMenu ? compareByRow[compareMenu.row] === item.path : false}
                  onClick={() => {
                    if (compareMenu) setCompareByRow((prev) => ({ ...prev, [compareMenu.row]: item.path }));
                    setCompareMenu(null);
                  }}
                  sx={{ pl: 3, maxWidth: 420 }}
                >
                  <Typography variant="body2" noWrap title={item.path}>
                    {fileName(item.path)}
                    {item.label ? ` · ${item.label}` : ""}
                  </Typography>
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled dense sx={{ pl: 3 }}>
                {t("CompareBasketEmptyMsg", "검증 리스트에서 이미지를 오른쪽 클릭해 담을 수 있습니다.")}
              </MenuItem>
            )}
            <Divider />
            {(["row", "column"] as const).map((layout) => (
              <MenuItem
                key={layout}
                dense
                selected={compareLayout === layout}
                onClick={() => {
                  setCompareLayout(layout);
                  setCompareMenu(null);
                }}
              >
                {layout === "row" ? t("CompareSideBySide", "가로로 나란히") : t("CompareStacked", "세로로 나란히")}
              </MenuItem>
            ))}
            {compareMenu && compareByRow[compareMenu.row] && (
              <MenuItem
                dense
                onClick={() => {
                  setCompareByRow(({ [compareMenu.row]: _, ...rest }) => rest);
                  setCompareMenu(null);
                }}
              >
                {t("CloseCompare", "비교 닫기")}
              </MenuItem>
            )}
          </Menu>
        </Box>
      </Dialog>
    </div>
  );
}

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.5)",
  backdropFilter: "blur(4px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1300,
};

const modalStyle: React.CSSProperties = {
  background: "var(--dk-surface, #fff)",
  width: 1100,
  maxHeight: "90vh",
  borderRadius: 16,
  boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};
